import { BACKEND_URL } from '../config/backendUrl';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { getErrorMessage, resolveBackendUrl } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { HyreDocsIcon } from '../components/HyreDocsLogo';
import { Loader2, CheckCircle2, AlertCircle, Download, FileStack } from 'lucide-react';

const MAX_PLACEMENTS = 100;

// The label of the template field this page fills in. Matched against the
// template's field labels (case-insensitively) server-side.
const WEEK_ENDING_LABEL = 'Week Ending Date';

// Dedicated instance (mirrors BullhornEmbedPage.js's embedApi) — this page
// runs inside a third-party Bullhorn iframe, where the session cookie set by
// auth.ts's /sso/mass-merge-redirect is routinely blocked. The `token` query
// param that redirect appends is the real auth path; withCredentials stays
// on only as a harmless fallback for the rare case this gets opened with a
// first-party session already present.
const mergeApi = axios.create({ baseURL: `${BACKEND_URL}/api`, withCredentials: true });

// Case-insensitive lookup — Bullhorn's own Custom List Action docs say it
// appends "EntityID"/"EntityType" to the configured URL, but admins can also
// hand-edit the URL (e.g. while testing), so this tolerates any casing
// rather than silently failing on "entityid" vs "EntityID".
function getParamCI(searchParams, name) {
  const target = name.toLowerCase();
  for (const [key, value] of searchParams.entries()) {
    if (key.toLowerCase() === target) return value;
  }
  return null;
}

const SSO_ERROR_MESSAGES = {
  missing_corporation: "Bullhorn didn't send a CorporationID with this action. Check the Custom List Action's URL configuration.",
  unknown_corporation: "This Bullhorn organization isn't connected to a HyreDocs tenant yet.",
  not_provisioned: "Your Bullhorn user couldn't be matched to a HyreDocs account. Ask an admin to check your access.",
};

// Landing page for a Bullhorn "Custom List Action" wired to the Candidate ->
// Placement grid (see Tools > View Layout > Placement > Custom Menu Actions
// in Bullhorn admin): a recruiter selects several placements, clicks the
// action, and Bullhorn posts to auth.ts's /sso/mass-merge-redirect, which
// resolves the tenant/user and redirects here with a signed `token` plus the
// selected placements' EntityID. The template to merge against is baked
// into the configured action URL itself as ?template_id=... (one fixed
// template per action) rather than something Bullhorn passes — Bullhorn has
// no concept of a HyreDocs template to select from.
//
// Nothing runs on load: the recruiter enters the Week Ending Date and clicks
// Generate. That date is sent as a template-field value (matched by label)
// and stamped onto every placement's block.
//
// Deliberately NOT wrapped in ProtectedRoute/TenantRouteWrapper (see
// App.js's /embed/mass-merge route) — those depend on the regular
// cookie-based AuthContext, which this third-party-iframe context can't
// reliably use. Auth here comes entirely from the `token` param.
export default function BullhornMassMergeActionPage() {
  const [searchParams] = useSearchParams();
  const [weekEnding, setWeekEnding] = useState('');
  const [state, setState] = useState({ status: 'ready' }); // ready | loading | error | done

  const templateId = getParamCI(searchParams, 'template_id');
  const entityType = getParamCI(searchParams, 'EntityType');
  const entityIdsRaw = getParamCI(searchParams, 'EntityID') ?? getParamCI(searchParams, 'entity_ids');
  const token = getParamCI(searchParams, 'token');
  const ssoError = getParamCI(searchParams, 'ssoError');

  // Problems with the link itself — shown straight away, no button.
  const { placementIds, linkError } = useMemo(() => {
    const ids = [...new Set((entityIdsRaw || '').split(',').map((s) => s.trim()).filter(Boolean))];
    let message = null;
    if (ssoError) message = SSO_ERROR_MESSAGES[ssoError] || `Could not sign you in (${ssoError}).`;
    else if (!token) message = 'This link is missing its sign-in token. Use the action from Bullhorn directly rather than this URL on its own.';
    else if (!templateId) message = 'This link is missing its template configuration. Check the Custom List Action URL configured in Bullhorn.';
    else if (entityType && entityType.toLowerCase() !== 'placement') message = `This action is wired to the wrong Bullhorn entity type (got "${entityType}", expected Placement). Check the Custom List Action's entity in Bullhorn admin.`;
    else if (!ids.length) message = 'No placements were selected in Bullhorn. Select one or more placements, then use the action again.';
    else if (ids.length > MAX_PLACEMENTS) message = `${ids.length} placements were selected, but this action supports up to ${MAX_PLACEMENTS} at a time. Select fewer placements and try again.`;
    return { placementIds: ids, linkError: message };
  }, [entityIdsRaw, ssoError, token, templateId, entityType]);

  const handleGenerate = async () => {
    setState({ status: 'loading' });
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const res = await mergeApi.post('/mass-merge/generate', {
        template_id: templateId,
        placement_ids: placementIds,
        field_values: { [WEEK_ENDING_LABEL]: weekEnding },
      }, { headers });
      // merge_url ("/api/completed/{key}") already carries the /api prefix,
      // so this goes through plain axios + resolveBackendUrl rather than
      // mergeApi (whose baseURL already ends in /api — using mergeApi here
      // produced a real .../api/api/completed/... 404, caught live).
      const fileRes = await axios.get(resolveBackendUrl(res.data.merge_url), { headers, responseType: 'blob' });
      setState({ status: 'done', ...res.data, requested: placementIds.length, blobUrl: URL.createObjectURL(fileRes.data) });
    } catch (error) {
      setState({ status: 'error', message: getErrorMessage(error, 'Could not generate the merged PDF.') });
    }
  };

  const status = linkError ? 'error' : state.status;
  const errorMessage = linkError || state.message;

  return (
    // No min-h-screen/vertical-centering here — this renders inside
    // Bullhorn's own (often short) action panel, not a full browser tab.
    // Forcing full viewport height pushed the card below the fold, so the
    // page is sized to its content instead (reported live: "the page is
    // huge, I have to scroll down to see anything").
    <div className="bg-[#F9F8F6] p-6">
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 w-12 h-12 rounded-xl bg-gradient-to-br from-forest to-forest/70 flex items-center justify-center">
            <HyreDocsIcon className="w-7 h-7" />
          </div>
          <CardTitle>Mass Merge from Bullhorn</CardTitle>
          <CardDescription>
            {status === 'ready' || status === 'loading'
              ? `${placementIds.length} placement${placementIds.length === 1 ? '' : 's'} selected.`
              : 'Generate a merged PDF for the placements you selected.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-center">
          {status === 'ready' && (
            <div className="flex flex-col items-center gap-4 py-2">
              <div className="w-full text-left space-y-2">
                <Label htmlFor="week-ending-date">{WEEK_ENDING_LABEL}</Label>
                <Input
                  id="week-ending-date"
                  type="date"
                  value={weekEnding}
                  onChange={(e) => setWeekEnding(e.target.value)}
                />
              </div>
              <Button onClick={handleGenerate} disabled={!weekEnding}>
                <FileStack className="w-4 h-4 mr-1" /> Generate PDF
              </Button>
            </div>
          )}

          {status === 'loading' && (
            <div className="flex flex-col items-center gap-3 py-4">
              <Loader2 className="w-8 h-8 animate-spin text-forest" />
              <p className="text-sm text-muted-foreground">Merging placements…</p>
            </div>
          )}

          {status === 'error' && (
            <div className="flex flex-col items-center gap-3 py-2">
              <AlertCircle className="w-8 h-8 text-destructive" />
              <p className="text-sm text-foreground">{errorMessage}</p>
              {!linkError && (
                <Button variant="outline" onClick={() => setState({ status: 'ready' })}>Try again</Button>
              )}
            </div>
          )}

          {status === 'done' && (
            <div className="flex flex-col items-center gap-3 py-2">
              <CheckCircle2 className="w-8 h-8 text-forest" />
              <p className="text-sm text-foreground">
                Merged {state.placements_merged} of {state.requested} placement{state.requested === 1 ? '' : 's'}.
                {state.placements_failed?.length ? ` ${state.placements_failed.length} could not be resolved.` : ''}
              </p>
              {state.field_values_unmatched?.length > 0 && (
                <p className="text-xs text-destructive">
                  This template has no "{state.field_values_unmatched.join('", "')}" field, so the date was not added to the PDF.
                </p>
              )}
              {/* A real <a download>, not window.open() — popups from inside
                  Bullhorn's iframe are blocked outright in some hosts even on
                  a direct click (see BullhornEmbedPage.js's openPreviewPdf). */}
              <a href={state.blobUrl} download="mass-merge.pdf">
                <Button>
                  <Download className="w-4 h-4 mr-1" /> Download merged PDF
                </Button>
              </a>
              {state.placements_failed?.length > 0 && (
                <ul className="text-xs text-muted-foreground text-left w-full mt-2 space-y-1">
                  {state.placements_failed.map((f) => (
                    <li key={f.placement_id}>Placement {f.placement_id}: {f.reason}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
